import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';

type SpeechToTextProps = {
    onTranscript?: (text: string, isFinal: boolean) => void;
    className?: string;
    lang?: string;
    showControls?: boolean;
    active?: boolean;
    height?: number; // wavesurfer canvas height
    windowSec?: number; // scrolling/continuous window size
    timesliceMs?: number; // media recorder timeslice for faster UI updates
};

export default function SpeechToText({ onTranscript, className, lang = "en-US", showControls = false, active = false, height = 48, windowSec = 2.5, timesliceMs = 60 }: SpeechToTextProps) {
    const waveformRef = useRef<HTMLDivElement>(null);
    const recognitionRef = useRef<any>(null);
    const recordRef = useRef<any>(null);
    const wavesurferRef = useRef<any>(null);
    const [transcript, setTranscript] = useState("");
    const [permission, setPermission] = useState<"granted" | "denied" | "prompt" | undefined>(undefined);
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [recordReady, setRecordReady] = useState(false);

    useEffect(() => {
        const container = waveformRef.current;
        if (!container) return;

        setRecordReady(false);

        const record = RecordPlugin.create({
            scrollingWaveform: true,
            scrollingWaveformWindow: windowSec,
            continuousWaveform: true,
            continuousWaveformDuration: windowSec,
            mediaRecorderTimeslice: timesliceMs,
        });

        const ws = WaveSurfer.create({
            container,
            waveColor: "#a3a3a3",        // correct grey
            progressColor: "transparent",
            interact: false,
            cursorWidth: 0,
            height,                      // 🔑 ALWAYS explicit
            width: Math.max(container.clientWidth || 120, 120), // ensure non-zero width for initial draw
            plugins: [record],
        });

        recordRef.current = record;
        wavesurferRef.current = ws;

        // Redraw when container gets dimensions (e.g. after flex layout)
        const ro = new ResizeObserver(() => {
            const w = container.clientWidth;
            if (w > 0 && wavesurferRef.current) {
                wavesurferRef.current.setOptions({ width: w });
            }
        });
        ro.observe(container);
        // Initial sync after layout
        const t = requestAnimationFrame(() => {
            const w = container.clientWidth;
            if (w > 0 && wavesurferRef.current) {
                wavesurferRef.current.setOptions({ width: w });
            }
        });

        setRecordReady(true);

        return () => {
            cancelAnimationFrame(t);
            ro.disconnect();
            ws.destroy();
            wavesurferRef.current = null;
            recordRef.current = null;
        };
    }, [height, windowSec, timesliceMs]);

    useEffect(() => {
        const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SR) return;
        const recog = new SR();
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = lang;
        recog.onresult = (event: any) => {
            let finalTranscript = "";
            for (let i = event.resultIndex; i < event.results.length; ++i) {
                finalTranscript += event.results[i][0].transcript;
            }
            setTranscript(finalTranscript);
            const last = event.results[event.results.length - 1];
            if (onTranscript) onTranscript(finalTranscript, !!last?.isFinal);
        };
        recognitionRef.current = recog;
    }, [lang, onTranscript]);

    const startListening = () => recognitionRef.current?.start?.();
    const stopListening = () => recognitionRef.current?.stop?.();

    // Start/stop mic + recognition when active changes
    useEffect(() => {
        const rec: any = recordRef.current;
        const recog: any = recognitionRef.current;
        if (!rec || !recog) return;
        const queryPermission = async (): Promise<"granted" | "denied" | "prompt" | undefined> => {
            try {
                // @ts-ignore - PermissionName typing
                const status = await (navigator as any).permissions?.query?.({ name: "microphone" });
                if (status) {
                    setPermission(status.state);
                    status.onchange = () => setPermission(status.state);
                    return status.state as any;
                }
            } catch { }
            return undefined;
        };
        const startAll = async () => {
            setErrorMsg("");
            await queryPermission();
            try { await rec.startMic(); }
            catch (err: any) {
                setErrorMsg(formatMicError(err, permission));
                return;
            }
            try { recog.start(); } catch { }
        };
        if (active) {
            startAll();
        } else {
            try { rec.stopMic(); } catch { }
            try { recog.stop(); } catch { }
            try {
                const s: MediaStream | undefined = (rec as any)?.stream;
                s?.getTracks?.().forEach((t) => t.stop());
            } catch { }
        }
        return () => {
            // Ensure release on effect cleanup
            try { rec.stopMic(); } catch { }
            try { recog.stop(); } catch { }
            try {
                const s: MediaStream | undefined = (rec as any)?.stream;
                s?.getTracks?.().forEach((t) => t.stop());
            } catch { }
        };
    }, [active, permission, recordReady]);

    const formatMicError = (err: any, perm?: "granted" | "denied" | "prompt") => {
        const name = err?.name || "";
        const msg = err?.message || "";
        if (perm === "denied" || name === "NotAllowedError") {
            return "Microphone access denied. Use the browser's Site settings (lock icon) to allow the microphone, then reload.";
        }
        if (name === "NotFoundError") return "No microphone found. Connect a mic and try again.";
        if (name === "NotReadableError" || name === "SecurityError") return "Cannot access the microphone. It may be in use by another app or blocked by the OS.";
        if (/denied|permission/i.test(msg)) return "Microphone permission was denied. Please allow and try again.";
        return "Failed to access the microphone. Please try again.";
    };

    return (
        <div className={`${className} overflow-hidden`}>
            <div ref={waveformRef} className="min-w-0 flex-1 overflow-hidden w-full flex-shrink-0" style={{ minHeight: height, height: `${height}px`, minWidth: 120 }} />
            {(showControls || errorMsg) && (
                <div className="mt-2 flex gap-2 justify-center">
                    {showControls && (
                        <>
                            <button onClick={startListening} className="px-3 py-1.5 bg-green-500 text-white rounded-md">Start</button>
                            <button onClick={stopListening} className="px-3 py-1.5 bg-red-500 text-white rounded-md">Stop</button>
                        </>
                    )}
                    {errorMsg && <span className="text-xs text-red-600">{errorMsg}</span>}
                </div>
            )}
            {showControls && (
                <p className="mt-2 text-sm text-gray-700"><strong>Transcript:</strong> {transcript}</p>
            )}
        </div>
    );
}


